/**
 * Stream D — D60: Website Crawl -> Auto-Setup
 *
 * Enter URL -> AI extracts brand, services, team, hours.
 * EU AI Act label. Real apiFetch. CSS vars only.
 */
import { useState } from "react";
import { apiFetch } from "@/lib/api-client";
import { Globe, Sparkles, CheckCircle2, Palette, Clock, Users, Briefcase } from "lucide-react";
import { Skeleton } from "@/components/skeleton";

interface CrawlResult {
  sessionId: string;
  businessName: string | null;
  industry: string | null;
  logoUrl: string | null;
  primaryColor: string | null;
  services: string[];
  teamMembers: string[];
  location: string | null;
  hours: string | null;
  description: string | null;
  sourceUrl: string;
}

interface AnalyzeResponse {
  sessionId: string;
  status: "suggestions_ready";
  extractedData: {
    businessName: string;
    industry: string;
    services: string[];
    hours: string;
    colors?: {
      primary?: string;
      secondary?: string;
    };
    logoUrl?: string | null;
    team?: Array<{ name: string; role?: string }>;
    metaDescription?: string;
  };
}

export default function WebsiteCrawlPage() {
  const [url, setUrl] = useState("");
  const [result, setResult] = useState<CrawlResult | null>(null);
  const [isCrawling, setIsCrawling] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);

  const handleCrawl = async () => {
    if (!url.trim()) return;
    setIsCrawling(true);
    setError(null);
    setResult(null);
    try {
      const sourceUrl = url.trim();
      const res = await apiFetch<AnalyzeResponse>("/api/onboarding/analyze", {
        method: "POST",
        body: JSON.stringify({ url: sourceUrl }),
      });
      setResult({
        sessionId: res.sessionId,
        businessName: res.extractedData.businessName || null,
        industry: res.extractedData.industry || null,
        logoUrl: res.extractedData.logoUrl ?? null,
        primaryColor: res.extractedData.colors?.primary ?? null,
        services: res.extractedData.services ?? [],
        teamMembers: (res.extractedData.team ?? []).map((member) => member.name).filter(Boolean),
        location: null,
        hours: res.extractedData.hours || null,
        description: res.extractedData.metaDescription ?? null,
        sourceUrl,
      });
    } catch {
      setError("Couldn't analyze this website. Check the URL and try again.");
    } finally {
      setIsCrawling(false);
    }
  };

  const handleApply = async () => {
    if (!result) return;
    setIsApplying(true);
    try {
      await apiFetch("/api/onboarding/confirm", {
        method: "POST",
        body: JSON.stringify({
          sessionId: result.sessionId,
          acceptedConfig: {
            businessName: result.businessName ?? "Your Business",
            industry: result.industry ?? "generic",
            services: result.services,
            hours: result.hours ?? undefined,
            colors: {
              primary: result.primaryColor ?? undefined,
            },
          },
        }),
      });
      setApplied(true);
    } catch {
      setError("Failed to apply settings");
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto py-12 space-y-8">
      <div className="text-center">
        <Globe className="mx-auto h-10 w-10 text-[var(--color-primary)] mb-3" />
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Import from Your Website</h1>
        <p className="text-sm text-[var(--color-muted-foreground)] mt-1">AI extracts your brand, services, and team to pre-fill your account</p>
      </div>

      {error && (
        <div
          role="alert"
          aria-live="assertive"
          className="rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-3 text-sm text-[var(--color-error)]"
        >
          {error}
        </div>
      )}

      <div className="flex gap-2">
        <input
          value={url}
          onChange={e => setUrl(e.target.value)}
          placeholder="https://yourbusiness.com"
          className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
        />
        <button type="button" onClick={handleCrawl} disabled={isCrawling || !url.trim()} className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-5 py-3 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50">
          {isCrawling ? (
            <>
              <Skeleton className="h-4 w-4 bg-white/50" />
              <Skeleton className="h-4 w-16 bg-white/50" />
            </>
          ) : "Analyze"}
        </button>
      </div>

      {result && !applied && (
        <>
          <div className="flex items-center gap-2 rounded-lg bg-[var(--color-ai)]/5 border border-[var(--color-ai)]/20 px-4 py-2">
            <Sparkles className="h-4 w-4 text-[var(--color-ai)] shrink-0" />
            <p className="text-xs text-[var(--color-ai)]">AI-extracted data — review before applying to your account</p>
          </div>

          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 space-y-3">
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]/60 px-3 py-2 text-xs text-[var(--color-muted-foreground)]">
              Source reviewed: <span className="font-medium text-[var(--color-foreground)]">{result.sourceUrl}</span>
            </div>
            {result.businessName && (
              <div className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-[var(--color-primary)]" /><span className="font-medium text-[var(--color-foreground)]">{result.businessName}</span></div>
            )}
            {result.primaryColor && (
              <div className="flex items-center gap-2"><Palette className="h-4 w-4 text-[var(--color-primary)]" /><span className="text-sm text-[var(--color-foreground)]">Brand color: </span><span className="inline-block h-5 w-5 rounded" style={{ backgroundColor: result.primaryColor }} /><span className="text-xs text-[var(--color-muted-foreground)] font-mono">{result.primaryColor}</span></div>
            )}
            {result.services.length > 0 && (
              <div><div className="flex items-center gap-2 mb-1"><Briefcase className="h-4 w-4 text-[var(--color-muted-foreground)]" /><span className="text-sm text-[var(--color-foreground)]">Services ({result.services.length})</span></div><div className="flex flex-wrap gap-1 ml-6">{result.services.map(s => <span key={s} className="rounded-full bg-[var(--color-surface)] px-2 py-0.5 text-xs text-[var(--color-foreground)]">{s}</span>)}</div></div>
            )}
            {result.teamMembers.length > 0 && (
              <div className="flex items-center gap-2"><Users className="h-4 w-4 text-[var(--color-muted-foreground)]" /><span className="text-sm text-[var(--color-foreground)]">{result.teamMembers.length} team members found</span></div>
            )}
            {result.hours && (
              <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-[var(--color-muted-foreground)]" /><span className="text-sm text-[var(--color-muted-foreground)]">{result.hours}</span></div>
            )}
          </div>

          <button type="button" onClick={handleApply} disabled={isApplying} className="w-full rounded-lg bg-[var(--color-primary)] py-3 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50">
            {isApplying ? (
              <span className="inline-flex items-center gap-2">
                <Skeleton className="h-4 w-4 bg-white/50" />
                <Skeleton className="h-4 w-24 bg-white/50" />
              </span>
            ) : "Apply to My Account"}
          </button>
        </>
      )}

      {applied && (
        <div className="rounded-xl border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 p-6 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-[var(--color-success)] mb-3" />
          <p className="font-medium text-[var(--color-foreground)]">Settings applied</p>
          <p className="text-sm text-[var(--color-muted-foreground)] mt-1">Your account has been pre-configured based on your website.</p>
        </div>
      )}
    </div>
  );
}
